<template>
    <div class="container w-full min-h-screen bg-[#11111b] text-white flex flex-col">
        <Header />

        <main id="hero-section" class="relative w-full min-h-screen flex items-start justify-center pt-4 px-6">
            <div class="absolute inset-0 bg-cover bg-center filter blur-sm z-0"
                :style="{ backgroundImage: `url(${backgroundImage})` }"></div>

            <div class="relative z-10 w-full max-w-5xl mx-auto flex flex-col items-start">
                <!-- Sticky Search Bar -->
                <div class="w-full sticky top-0 bg-[#11111b] z-20 p-4 shadow-md">

                    <input type="text" v-model="searchQuery" placeholder="Search blogs..."
                        class="w-full p-3 text-sm sm:text-base bg-gray-800 border border-gray-700 rounded focus:outline-none focus:border-cyan-400" />

                </div>

                <!-- Blog Cards -->
                <section v-if="filteredCount"
                    class="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-6">
                    <router-link v-for="blog in paginatedBlogs" :key="blog.id" :to="`/blog-posts/${blog.id}`"
                        class="service-card p-4 rounded-lg cursor-pointer min-h-0 hover:shadow-lg transition-shadow bg-[#1e1e2f]">
                        <div class="w-full h-40 overflow-hidden rounded-md mb-3">
                            <img :src="blog.imageURL || defaultImage" alt="Blog"
                                class="w-full h-full object-cover rounded-md" />
                        </div>
                        <h3 class="text-base font-semibold mb-2 truncate">{{ blog.title }}</h3>
                        <p class="text-xs sm:text-sm text-gray-400 mb-2">{{ truncateText(blog.description, 120) }}</p>
                        <p class="text-xs text-gray-500">{{ formatDate(blog.createdAt) }}</p>
                    </router-link>
                </section>

                <div v-if="isSearching" class="w-full flex flex-col items-center justify-center py-12 min-h-full">
                    <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-cyan-400"></div>
                    <p class="mt-4 text-sm sm:text-base text-gray-400">Searching...</p>
                </div>

                <div v-else-if="isLoading" class="w-full flex flex-col items-center justify-center py-12 min-h-full">
                    <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-cyan-400"></div>
                    <p class="mt-4 text-sm sm:text-base text-gray-400">Loading blogs...</p>
                </div>

                <div v-else-if="!filteredCount"
                    class="w-full flex flex-col items-center justify-center py-12 min-h-full">
                    <p class="mt-4 text-sm sm:text-base text-gray-400">No blogs found.</p>
                </div>

                <!-- Pagination -->
                <div class="mt-8 w-full flex justify-center">
                    <div class="relative inline-flex items-center px-4 py-2 text-sm font-semibold" v-if="filteredCount">
                        <SimplePagination :current-page="currentPage" :page-size="pageSize" :total-items="filteredCount"
                            @update:currentPage="currentPage = $event" />
                    </div>
                </div>
            </div>
        </main>

        <ClientFooter />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Header from '@/components/Header.vue';
import ClientFooter from '@/components/ClientFooter.vue';
import SimplePagination from '@/components/SimplePagination.vue';
import { projectFirestore } from '@/firebase/config.js';
import { collection, getDocs, query, where } from 'firebase/firestore';

// Background Image
import backgroundImage from '@/assets/images/background.png';

const defaultImage = '@/assets/images/default.png';

const searchQuery = ref('');
const blogs = ref([]);
const currentPage = ref(1);
const pageSize = ref(8);
const isSearching = ref(false);
const isLoading = ref(true);
let searchTimer = null;

const loadBlogs = async () => {
    try {
        isLoading.value = true;
        const blogsCol = collection(projectFirestore, 'blogs');
        const q = query(blogsCol, where('status', '==', true));
        const snapshot = await getDocs(q);
        blogs.value = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
        console.error('Error loading blogs:', err);
    } finally {
        isLoading.value = false;
    }
};

const textFromHtml = (html = '') => {
    try {
        const div = document.createElement('div');
        div.innerHTML = html;
        return (div.textContent || div.innerText || '').trim();
    } catch (e) {
        return '';
    }
};

const truncateText = (html = '', max = 100) => {
    const text = textFromHtml(html);
    return text.length > max ? text.slice(0, max).trim() + '...' : text;
};

const filtered = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return blogs.value;
    return blogs.value.filter(b => {
        const title = (b.title || '').toLowerCase();
        const desc = textFromHtml(b.description || '').toLowerCase();
        return title.includes(q) || desc.includes(q);
    });
});

const filteredCount = computed(() => filtered.value.length);

const paginatedBlogs = computed(() => {
    const start = (currentPage.value - 1) * pageSize.value;
    return filtered.value.slice(start, start + pageSize.value);
});

const formatDate = (ts) => {
    try {
        if (!ts) return '';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleDateString();
    } catch (e) {
        return '';
    }
};

onMounted(() => {
    loadBlogs();
});

// Reset to first page when search changes
watch(searchQuery, () => {
    // show searching state and reset page
    isSearching.value = true;
    currentPage.value = 1;
    if (searchTimer) clearTimeout(searchTimer);
    // debounce hiding the searching indicator
    searchTimer = setTimeout(() => {
        isSearching.value = false;
    }, 350);
});
</script>

<style scoped>
.container {
    width: 100%;
    max-width: none;
    min-height: 100vh;
}

.service-card {
    background-color: #1e1e2f;
    border: 1px solid rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    color: #ffffff;
}

.service-card:hover {
    transform: translateY(-4px);
}

.prose a {
    color: #06b6d4;
}

@media (max-width: 640px) {
    .grid-cols-1 {
        grid-template-columns: repeat(1, minmax(0, 1fr));
    }
}

@media (min-width: 641px) and (max-width: 768px) {
    .grid-cols-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 769px) and (max-width: 1024px) {
    .grid-cols-3 {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (min-width: 1025px) {
    .grid-cols-4 {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}
</style>